import { webappClient, sourcesClient } from '@lib/bugsnag';
import { stores } from '@stores/index';

const xRequestIdKey = 'x-request-id';

type Headers = {
  [xRequestIdKey]: string;
};

export type RequestConfig = {
  url?: string;
  baseURL?: string;
  method?: string;
  startTime?: Date;
  timeout?: number;
  startVisibility?: string;
};

interface ApiError extends Error {
  response:
    | {
        status: number;
        data: Record<string, unknown> | string;
        headers: Headers;
      }
    | undefined;
  config: RequestConfig;
  code?: string;
}

const Logger = {
  logApi(error: ApiError) {
    const request = getRequestObject(error.config, error.response?.headers[xRequestIdKey] || '');
    error.response ? logBackendError(error, request) : logTimeouts(error, request);
  },

  log(
    error: Error,
    description: string,
    metaData?: Record<string, unknown>,
    client = webappClient,
  ) {
    client.addMetadata('user', { tier: stores.organizationStore.prettyPrintTier() });
    client.leaveBreadcrumb(description, metaData);
    error.message = `${description} - ${error.message}`;
    client.notify(error);
  },
};

export default Logger;

const getRequestObject = (config: ApiError['config'], requestId: string) => ({
  baseURL: config?.baseURL,
  url: config?.url,
  method: config?.method,
  responseTime: (new Date().valueOf() - config.startTime!.valueOf() || 0) / 1000,
  timeout: config.timeout! / 1000,
  visibility: {
    start: config.startVisibility,
    end: document.visibilityState,
  },
  requestId,
});

const logTimeouts = (error: ApiError, request: ReturnType<typeof getRequestObject>) => {
  if (request.url?.includes('v1/session')) {
    return;
  }
  if (request.responseTime > request.timeout && request.visibility.start !== 'hidden') {
    Logger.log(error, error.code || 'No error code', request, getClient(request.url || ''));
  }
};

const logBackendError = (error: ApiError, request: ReturnType<typeof getRequestObject>) => {
  const { status, data } = error.response!;
  const metaData = {
    request,
    response: {
      status,
      data,
    },
  };
  Logger.log(error, request.url!, metaData, getClient(request.url || ''));
};

const getClient = (url: string) =>
  url.includes('cloudSources/') || url.includes('integrationsInfo/') ? sourcesClient : webappClient;
